import { useEffect } from 'react';
import classnames from 'clsx';
import { useDispatch } from 'react-redux';

import { ServiceCollectionService } from './service-collection-service';
import { PrimaryActionButtonContainer } from '../../containers/shipment-configuration-widget/primary-action-button-container';
import { ComponentStates } from '../../enums/component-states';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../hooks/use-has-rate-shopper-access-level';
import { useIsLocalDeliveryServiceSelectedForFulfillmentPlan } from '../../hooks/use-is-local-delivery-service-selected-for-fulfillment-plan';
import * as Actions from '../../store/csw/csw.actions';
import { useFulfillmentPlannerContext } from '../context';
import { CostReviewLink, getShouldDisableCostReview } from '../cost-review-link';
import { useIsTikTokShippingOrder } from '../hooks/use-is-tiktok-shipping-order';
import style from './style.module.less';
import { FulfillmentPlannerContextDeliveryTimeContainer } from '../delivery-time/container';
import { DeliveryTimeLabel } from '../delivery-time/label';
import { useIsLocalDeliveryServiceSelected } from '../hooks/use-is-local-delivery-service-selected';
import { FulfillmentPlannerContextPickupTimeContainer } from '../pickup-time/container';
import { PickupTimeLabel } from '../pickup-time/label';
import { Rate } from '../rate-consumer';
import { FulfillmentPlanRateRecommendations } from '../rate-recommendation/recommendation/fulfillment-plan-rate-recommendations';

/**
 * @param {object} props
 * @param {string} props.fulfillmentPlanId
 * @param {string} [props.className]
 * @param {boolean} [props.isQuickshipCompatible]
 * @param {boolean} [props.hideCostReviewLink]
 * @returns {React.JSX.Element}
 */
export const RateAndCreateLabel = ({ fulfillmentPlanId, className = null, isQuickshipCompatible = false, hideCostReviewLink = false }) => {
  const dispatch = useDispatch();
  const isPickupServiceSelectedInEntities = useIsLocalDeliveryServiceSelectedForFulfillmentPlan(fulfillmentPlanId);
  const isPickupServiceSelectedInCSW = useIsLocalDeliveryServiceSelected();
  const isPickupServiceSelected = isPickupServiceSelectedInEntities || isPickupServiceSelectedInCSW;
  const {
    uiRestrictions,
    serviceCollectionGuid: selectedServiceCollectionGuid,
    isRating,
    rateErrors,
    totalCost,
  } = useFulfillmentPlannerContext();
  const hasRateShopperAccess = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);
  const shouldShowRateShopperSelectedService = hasRateShopperAccess && !!selectedServiceCollectionGuid;
  const isTikTokShipping = useIsTikTokShippingOrder(fulfillmentPlanId);
  const rateProvided = uiRestrictions?.rateSummary?.componentState === ComponentStates.Provided && !isTikTokShipping;

  const isCostReviewDisabled = getShouldDisableCostReview({
    isRating,
    hasRateErrors: Boolean(rateErrors?.length),
    hasRate: Boolean(totalCost?.value),
    hasProvidedRate: rateProvided,
  });

  useEffect(() => {
    dispatch(Actions.cswCompareRates(fulfillmentPlanId));
  }, [fulfillmentPlanId, dispatch]);

  return (
    <>
      <div className={classnames(style.footer, className)}>
        <Rate />
        <CostReviewLink
          className={classnames({
            [style['hide-cost-review-link']]: hideCostReviewLink,
          })}
          fulfillmentPlanIds={[fulfillmentPlanId]}
          linkDisabled={isCostReviewDisabled}
        />
        <div>
          <PrimaryActionButtonContainer fulfillmentPlanId={fulfillmentPlanId} isQuickshipCompatible={isQuickshipCompatible} />
        </div>
      </div>
      <div className={style['misc-section']}>
        {shouldShowRateShopperSelectedService ? (
          <ServiceCollectionService
            className={classnames({
              [style['service-alone']]: !rateProvided && isPickupServiceSelected,
            })}
            dataClassName={style['service-name']}
            fulfillmentPlanId={fulfillmentPlanId}
          />
        ) : null}
        {!rateProvided && (
          <>
            {isPickupServiceSelected ? (
              <div>
                <PickupTimeLabel />
                <FulfillmentPlannerContextPickupTimeContainer className={style['pickup-or-delivery-time']} />
              </div>
            ) : null}
            <div>
              <FulfillmentPlanRateRecommendations
                className={style['rate-recommendation']}
                fulfillmentPlanId={fulfillmentPlanId}
                theme='light'
              />
              <DeliveryTimeLabel />
              <FulfillmentPlannerContextDeliveryTimeContainer className={style['pickup-or-delivery-time']} />
            </div>
          </>
        )}
      </div>
    </>
  );
};
